import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { getPortalConfig } from './lib/api';
import type { PortalConfig } from './lib/types';
import DynamicNav, { NavItem } from './components/DynamicNav';
import SearchModal from './components/SearchModal';
import TutorOnDemand from './components/TutorOnDemand';
import Careers from './components/Careers';
import SevaGigs from './components/SevaGigs';
import RajaDashboard from './components/raja/RajaDashboard';
import ProductsShowcase from './components/ProductsShowcase.tsx';
import MarkdownPage from './components/MarkdownPage.tsx';
import SanskritGame from './components/SanskritGame.tsx';
import NavagrahChakra from './components/NavagrahChakra.tsx';
import AmarkoshaFppGame from './components/AmarkoshaFppGame.tsx';
import LoginModal from './components/LoginModal';
import ProfileGatewayPage from './components/ProfileGateway';
import OnboardingFlow from './components/OnboardingFlow';
import UserDiscovery from './components/UserDiscovery';
import JourneyPage from './components/JourneyPage';
import ConsentNotice, { getConsentRecord } from './components/ConsentNotice';
import HomePage from './components/HomePage';
import DhanurvedaProgram from './components/DhanurvedaProgram';
import DhanurvedaTerms from './components/DhanurvedaTerms';
import DhanurvedaBooking from './components/DhanurvedaBooking';
import ZohoContactForm from './components/ZohoContactForm';
import SanskritStudio from './components/SanskritStudio';
import SamvadLanding from './components/SamvadLanding';
import SamvadOnboarding from './components/SamvadOnboarding';
import SamvadSessionLogger from './components/SamvadSessionLogger';
import SamvadDashboard from './components/SamvadDashboard';
import SamvadContentEditor from './components/SamvadContentEditor';
import CurriculumDeck from './components/CurriculumDeck';
import SinghAmaraBook from './components/SinghAmaraBook';
import VyaghraBook from './components/VyaghraBook';
import VaraahaBook from './components/VaraahaBook';
import VaanaraBook from './components/VaanaraBook';
import HanumanBahukBook from './components/HanumanBahukBook';
import VanyagraamyaPashuBook from './components/VanyagraamyaPashuBook';
import NishedhaMadiraBook from './components/NishedhaMadiraBook';
import SaagarManthanBook from './components/SaagarManthanBook';
import GedhBook from './components/GedhBook';
import MaataaPitaaGuruBook from './components/MaataaPitaaGuruBook';
import NrivargBook from './components/NrivargBook';
import KshatriyavargBook from './components/KshatriyavargBook';
import VaishyavargKhand1Book from './components/VaishyavargKhand1Book';
import VaishyavargKhand2Book from './components/VaishyavargKhand2Book';
import VaishyavargKhand3Book from './components/VaishyavargKhand3Book';
import ParasharGeetaBook from './components/ParasharGeetaBook';
import PanchakoshaWorkbookBook from './components/PanchakoshaWorkbookBook';
import LaukikaNyayaBook from './components/LaukikaNyayaBook';
import AryaLaghukhagaBook from './components/AryaLaghukhagaBook';
import AryaSvarnamanjushaBook from './components/AryaSvarnamanjushaBook';
import AryaRahasyamayiGuhaBook from './components/AryaRahasyamayiGuhaBook';
import AdminDashboard from './components/AdminDashboard';
import SangatiPage from './components/SangatiPage';
import WallFeedPage from './components/WallFeedPage';
import PublicProfileView from './components/PublicProfileView';
import EducationalTrips from './components/EducationalTrips';
import SacredSites from './components/SacredSites';
import ImpactDashboard from './components/ImpactDashboard';
import BookingFlow from './components/BookingFlow';
import { useAuth } from './contexts/AuthContext';
import { useGlossary } from './contexts/GlossaryContext';
import { calculateGatewayProgress, computeSevaProviderStatus } from './lib/gatewayPolicy';
import { localizePortalConfig } from './lib/portalI18n';
import { storageGet, storageSet } from './lib/browserCompat';

import XSignInButton from "./components/XSignInButton";
import SanskritTranslator from './components/SanskritTranslator';

import './index.css';

type Page = 'home' | 'curriculum' | 'sangati' | 'journey' | 'tutor' | 'careers' | 'seva-gigs' | 'raja-dashboard' | 'products' | 'contact' | 'about' | 'privacy' | 'terms' | 'data-deletion' | 'faq' | 'spokensanskritgame' | 'navagrahchakra' | 'profile' | 'onboarding' | 'discovery' | 'wallfeed' | 'dhanurveda' | 'dhanurvedaterms' | 'dhanurvedabooking' | 'studio' | 'samvad' | 'samvad-onboarding' | 'samvad-session-log' | 'samvad-dashboard' | 'samvad-content-editor' | 'singh-amara' | 'vyaghra-valmiki' | 'varaaha-samvad' | 'vaanara-samvad' | 'hanuman-bahuk' | 'vanyagraamya-pashu' | 'nishedha-madira' | 'saagar-manthan' | 'geedh' | 'maataa-pitaa-guru' | 'nrivarg' | 'kshatriyavarg' | 'vaishyavarg-khand1' | 'vaishyavarg-khand2' | 'vaishyavarg-khand3' | 'parashar-geeta' | 'panchakosha-workbook' | 'laukika-nyaya' | 'arya-laghukhaga' | 'arya-svarnamanjusha' | 'arya-rahasyamayi-guha' | 'educational-trips' | 'sacred-sites' | 'impact' | 'booking' | 'admin' | 'public-profile' | 'sanskrit-translator' | 'amarkosha-fpp'; // Module-level map so it can be reused by popstate handler and initial URL parse

type Theme = 'dark' | 'light';

const PAGE_MAP: Record<string, Page> = {
  '/': 'home',
  '/curriculum': 'curriculum',
  '/sangati': 'sangati',
  '/journey': 'journey',
  '/tutor': 'tutor',
  '/careers': 'careers',
  '/seva-gigs': 'seva-gigs',
  '/products': 'products',
  '/about': 'about',
  '/contact': 'contact',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/data-deletion': 'data-deletion',
  '/faq': 'faq',
  '/curriculum/spokensanskritgame': 'spokensanskritgame',
  '/curriculum/navagrahchakra': 'navagrahchakra',
  '/products/tools/game': 'amarkosha-fpp',
  '/products/tools/game/': 'amarkosha-fpp',
  '/profile': 'profile',
  '/onboarding': 'onboarding',
  '/discovery': 'discovery',
  '/wall-feed': 'wallfeed',
  '/rajdharma/dhanurveda': 'dhanurveda',
  '/rajdharma/dhanurveda/': 'dhanurveda',
  '/rajdharma/dhanurveda/terms': 'dhanurvedaterms',
  '/rajdharma/dhanurveda/booking': 'dhanurvedabooking',
  '/studio': 'studio',
  '/samvad': 'samvad',
  '/samvad/onboarding': 'samvad-onboarding',
  '/samvad/session-log': 'samvad-session-log',
  '/samvad/dashboard': 'samvad-dashboard',
  '/samvad/content-editor': 'samvad-content-editor',
  '/products/singh-amara': 'singh-amara',
  '/products/vyaghra-valmiki': 'vyaghra-valmiki',
  '/products/varaaha-samvad': 'varaaha-samvad',
  '/products/vaanara-samvad': 'vaanara-samvad',
  '/products/hanuman-bahuk': 'hanuman-bahuk',
  '/products/vanyagraamya-pashu': 'vanyagraamya-pashu',
  '/products/nishedha-madira': 'nishedha-madira',
  '/products/saagar-manthan': 'saagar-manthan',
  '/products/geedh': 'geedh',
  '/products/maataa-pitaa-guru': 'maataa-pitaa-guru',
  '/products/nrivarg': 'nrivarg',
  '/products/kshatriyavarg': 'kshatriyavarg',
  '/products/vaishyavarg-khand1': 'vaishyavarg-khand1',
  '/products/vaishyavarg-khand2': 'vaishyavarg-khand2',
  '/products/vaishyavarg-khand3': 'vaishyavarg-khand3',
  '/products/parashar-geeta':    'parashar-geeta',
  '/products/panchakosha-workbook': 'panchakosha-workbook',
  '/products/laukika-nyaya':     'laukika-nyaya',
  '/products/arya-laghukhaga':   'arya-laghukhaga',
  '/products/arya-svarnamanjusha': 'arya-svarnamanjusha',
  '/products/arya-rahasyamayi-guha': 'arya-rahasyamayi-guha',
  '/educational-trips': 'educational-trips',
  '/sacred-sites':      'sacred-sites',
  '/impact':            'impact',
  '/booking':           'booking',
  '/admin': 'admin',
  '/tools/sanskrit-translator': 'sanskrit-translator',
};

const HASH_SCROLL_RETRY_MS = 150;
const HASH_SCROLL_TIMEOUT_MS = 5000;
const THEME_STORAGE_KEY = 'garuda_theme';
const GATED_PATHS = new Set(['/sangati', '/journey', '/tutor']);

function normalizeHash(hash: string): string {
  const rawHash = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!rawHash) return '';

  try {
    return decodeURIComponent(rawHash);
  } catch {
    return rawHash;
  }
}

function getInitialTheme(): Theme {
  const stored = storageGet(THEME_STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'dark';
}

interface AppState {
  config: PortalConfig | null;
  loading: boolean;
  error: string | null;
  currentPage: Page;
}

function App() {
  const { t, locale, locales, setLocale } = useGlossary();
  const [state, setState] = useState<AppState>({
    config: null,
    loading: true,
    error: null,
    currentPage: 'home',
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showConsentNotice, setShowConsentNotice] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchToken, setSearchToken] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, profile, policy, authLoading, isAdmin, adminLoading, emailLinkPrompt } = useAuth();
  const [viewAlias, setViewAlias] = useState<string>('');

  // Keep a fresh idToken for search auth-gating
  useEffect(() => {
    if (!user) { setSearchToken(null); return; }
    user.getIdToken().then(setSearchToken).catch(() => setSearchToken(null));
  }, [user]);

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  /** Resolve a pathname to a Page, handling the /u/:alias pattern. */
  const resolvePath = useCallback((pathname: string): Page => {
    if (PAGE_MAP[pathname]) return PAGE_MAP[pathname];
    if (pathname.startsWith('/u/')) return 'public-profile';
    return 'home';
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profile, user]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    storageSet(THEME_STORAGE_KEY, theme);
  }, [theme]);

  // Zoho SalesIQ live chat widget — India data centre (salesiq.zoho.in)
  // Activated only when VITE_ZOHO_SALESIQ_WIDGET_CODE is set in .env
  useEffect(() => {
    const widgetCode = import.meta.env.VITE_ZOHO_SALESIQ_WIDGET_CODE as string | undefined;
    if (!widgetCode || document.getElementById('zsiqscript')) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    w.$zoho = w.$zoho ?? {};
    w.$zoho.salesiq = { widgetcode: widgetCode, values: {}, ready() {} };

    const s = document.createElement('script');
    s.id = 'zsiqscript';
    s.defer = true;
    // MUST use salesiq.zoho.in (India data centre) — do NOT change to .com
    s.src = 'https://salesiq.zoho.in/widget';
    document.head.appendChild(s);

    return () => {
      document.getElementById('zsiqscript')?.remove();
    };
  }, []);

  // Load portal configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        console.log('Loading portal configuration...');
        const config = await getPortalConfig(locale);
        setState((prev) => ({
          ...prev,
          config,
          loading: false,
          error: null,
        }));
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : 'Failed to load configuration';
        console.error('Error loading config:', errorMsg);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMsg,
        }));
      }
    };

    loadConfig();
  }, [locale]);

  // ── Scroll to hash anchor after navigation ─────────────────────
  function scrollToHash(hash: string) {
    const normalizedHash = normalizeHash(hash);
    if (!normalizedHash) return;

    const startedAt = Date.now();

    const attempt = () => {
      const el = document.getElementById(normalizedHash);
      if (el) {
        window.requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      } else if (Date.now() - startedAt < HASH_SCROLL_TIMEOUT_MS) {
        window.setTimeout(attempt, HASH_SCROLL_RETRY_MS);
      }
    };

    window.setTimeout(attempt, 80);
  }

  // ── Core navigation: update state + browser URL ──────────────
  const handleNavigate = useCallback((path: string) => {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    const url = new URL(path, window.location.origin);
    const pathPart = url.pathname;
    const hash = url.hash ? url.hash.slice(1) : '';
    const gate = profile ? calculateGatewayProgress(profile, policy) : null;
    const gatedPath = GATED_PATHS.has(pathPart) && user && profile && gate && !gate.ready
      ? `/profile?return_to=${encodeURIComponent(`${url.pathname}${url.search}${url.hash}`)}`
      : '';

    const page = gatedPath ? 'profile' : resolvePath(pathPart);

    if (!page) {
      console.error(`Navigation error: Unknown path "${pathPart}". Valid: ${Object.keys(PAGE_MAP).join(', ')}`);
      setState((prev) => ({
        ...prev,
        error: `Page not found: "${pathPart}".`,
        currentPage: 'home',
      }));
      window.history.pushState({}, '', '/');
      return;
    }

    if (page === 'public-profile') {
      setViewAlias(pathPart.replace(/^\/u\//, ''));
    }

    // Push to browser history so back/forward and copy-URL work
    window.history.pushState({}, '', gatedPath || `${url.pathname}${url.search}${url.hash}`);

    setState((prev) => ({ ...prev, currentPage: page, error: null }));

    if (gatedPath) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (hash) {
      scrollToHash(hash);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [policy, profile, user]);

  // ── Handle browser back / forward buttons ────────────────────
  useEffect(() => {
    const onPop = () => {
      const pathPart = window.location.pathname;
      const hash     = window.location.hash.slice(1);
      if (GATED_PATHS.has(pathPart) && user && profile && !calculateGatewayProgress(profile, policy).ready) {
        const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        handleNavigate(`/profile?return_to=${encodeURIComponent(nextPath)}`);
        return;
      }
      const page     = resolvePath(pathPart);
      if (page === 'public-profile') setViewAlias(pathPart.replace(/^\/u\//, ''));
      setState((prev) => ({ ...prev, currentPage: page, error: null }));
      if (hash) scrollToHash(hash);
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [policy, profile, user, handleNavigate]);

  // Home is always reachable — removed auto-redirect to /journey so the
  // brand logo reliably returns logged-in users to the home page.

  useEffect(() => {
    if (authLoading || !user || !profile) return;
    if (!GATED_PATHS.has(window.location.pathname)) return;
    if (calculateGatewayProgress(profile, policy).ready) return;
    const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    handleNavigate(`/profile?return_to=${encodeURIComponent(nextPath)}`);
  }, [authLoading, user, profile, policy, handleNavigate]);

  useEffect(() => {
    if (emailLinkPrompt) {
      setShowLoginModal(true);
    }
  }, [emailLinkPrompt]);

  useEffect(() => {
    if (user && profile) {
      setShowLoginModal(false);
    }
  }, [user, profile]);

  // ── Read initial URL on first load (deep-link entry) ─────────
  useEffect(() => {
    const pathPart = window.location.pathname;
    const hash     = window.location.hash.slice(1);
    const page     = resolvePath(pathPart);
    if (page === 'public-profile') setViewAlias(pathPart.replace(/^\/u\//, ''));
    if (page && page !== 'home') {
      setState((prev) => ({ ...prev, currentPage: page }));
      if (hash) scrollToHash(hash);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPage = () => {
    const studioParams = new URLSearchParams(window.location.search);
    const studioSeedId = studioParams.get('seed_id') ?? '';
    const studioMode = studioParams.get('mode') === 'view' ? 'view' : 'edit';
    const studioTabParam = studioParams.get('tab');
    const studioTab = studioTabParam === 'process1' || studioTabParam === 'process2'
      ? studioTabParam
      : (studioMode === 'view' ? 'process2' : 'process1');
    const studioSegmentIdValue = studioParams.get('segment_id');
    const studioSegmentId = studioSegmentIdValue && Number.isFinite(Number(studioSegmentIdValue))
      ? Number(studioSegmentIdValue)
      : null;

    if (state.loading) {
      return (
        <div className="page-container loading-page">
          <p>{t('app.loading')}</p>
        </div>
      );
    }

    if (state.error) {
      return (
        <div className="page-container error-page">
          <div className="error-box">
            <h2>{t('app.error.title')}</h2>
            <p>{state.error}</p>
            <div className="error-actions">
              <button onClick={() => handleNavigate('/')}>{t('app.error.goHome')}</button>
              <button onClick={() => window.location.reload()}>{t('app.error.reload')}</button>
            </div>
          </div>
        </div>
      );
    }

    if (!state.config) {
      return (
        <div className="page-container error-page">
          <p>{t('app.error.noConfig')}</p>
        </div>
      );
    }

    switch (state.currentPage) {
      case 'home':
        return (
          <div className="page-container">
            <HomePage onNavigate={handleNavigate} />
          </div>
        );

      case 'curriculum':
        const curriculumOffering = localizedConfig?.service_offerings.find(
          (o) => o.id === 'राजृषि शिक्षा अभ्यासक्रम'
        );
        return (
          <div className="page-container curriculum-page">
            <CurriculumDeck module={curriculumOffering?.modules?.[0]} onNavigate={handleNavigate} />
          </div>
        );

      case 'tutor':
        return (
          <div className="page-container tutor-page">
            <TutorOnDemand onNavigate={handleNavigate} />
          </div>
        );

      case 'sangati':
        return (
          <div className="page-container">
            <SangatiPage
              onNavigate={handleNavigate}
              onNavigateHome={() => handleNavigate('/')}
              onNavigateTutor={() => handleNavigate('/tutor')}
            />
          </div>
        );

      case 'journey':
        return (
          <div className="page-container">
            <JourneyPage onNavigate={handleNavigate} />
          </div>
        );

      case 'careers':
        return (
          <div className="page-container careers-page">
            <Careers />
          </div>
        );
      case 'seva-gigs':
        return (
          <div className="page-container seva-gigs-page-container">
            <SevaGigs />
          </div>
        );
      case 'raja-dashboard':
        return (
          <div className="page-container">
            <RajaDashboard />
          </div>
        );
      case 'products':
        return (
          <div className="page-container products-page">
            <ProductsShowcase onNavigate={handleNavigate} />
          </div>
        );
      case 'contact':
        return <ZohoContactForm />;
      case 'about':
        return <MarkdownPage page="about" />;
      case 'privacy':
        return <MarkdownPage page="privacy" />;
      case 'terms':
        return <MarkdownPage page="terms" />;
      case 'data-deletion':
        return <MarkdownPage page="data-deletion" />;
      case 'faq':
        return <MarkdownPage page="faq" />;
      case 'spokensanskritgame':
        return (
          <div className="page-container">
            <SanskritGame />
          </div>
        );
      case 'navagrahchakra':
        return <NavagrahChakra />;
      case 'amarkosha-fpp':
        return (
          <div className="page-container">
            <AmarkoshaFppGame
              onNavigate={handleNavigate}
              onRequireSignIn={() => {
                if (getConsentRecord()) setShowLoginModal(true);
                else setShowConsentNotice(true);
              }}
            />
          </div>
        );
      case 'profile':
        return (
          <div className="page-container">
            <ProfileGatewayPage
              onNavigate={(path) => handleNavigate(path)}
              onNavigateHome={() => handleNavigate('/')}
            />
          </div>
        );
      case 'onboarding':
        return (
          <div className="page-container">
            <OnboardingFlow
              onNavigate={handleNavigate}
              onNavigateHome={() => handleNavigate('/')}
            />
          </div>
        );
      case 'discovery':
        return (
          <div className="page-container">
            <UserDiscovery onNavigateHome={() => handleNavigate('/')} />
          </div>
        );
      case 'wallfeed':
        return <WallFeedPage onNavigate={handleNavigate} />;
      case 'dhanurveda':
        return (
          <div className="page-container">
            <DhanurvedaProgram onNavigate={handleNavigate} />
          </div>
        );
      case 'dhanurvedaterms':
        return (
          <div className="page-container">
            <DhanurvedaTerms onNavigate={handleNavigate} />
          </div>
        );
      case 'dhanurvedabooking':
        return (
          <div className="page-container">
            <DhanurvedaBooking onNavigate={handleNavigate} />
          </div>
        );
      case 'studio':
        return (
          <SanskritStudio
            onNavigate={handleNavigate}
            initialSeedId={studioSeedId}
            initialViewMode={studioMode}
            initialSegmentId={studioSegmentId}
            initialTab={studioTab}
          />
        );
      case 'samvad':
        return (
          <div className="page-container">
            <SamvadLanding onNavigate={handleNavigate} />
          </div>
        );
      case 'samvad-onboarding':
        return (
          <div className="page-container">
            <SamvadOnboarding onNavigate={handleNavigate} />
          </div>
        );
      case 'samvad-session-log':
        return (
          <div className="page-container">
            <SamvadSessionLogger onNavigate={handleNavigate} />
          </div>
        );
      case 'samvad-dashboard':
        return (
          <div className="page-container">
            <SamvadDashboard onNavigate={handleNavigate} />
          </div>
        );
      case 'samvad-content-editor':
        return (
          <div className="page-container">
            <SamvadContentEditor onNavigate={handleNavigate} />
          </div>
        );
      case 'singh-amara':
        return (
          <div className="page-container">
            <SinghAmaraBook onNavigate={handleNavigate} />
          </div>
        );
      case 'vyaghra-valmiki':
        return (
          <div className="page-container">
            <VyaghraBook onNavigate={handleNavigate} />
          </div>
        );
      case 'varaaha-samvad':
        return (
          <div className="page-container">
            <VaraahaBook onNavigate={handleNavigate} />
          </div>
        );
      case 'vaanara-samvad':
        return (
          <div className="page-container">
            <VaanaraBook onNavigate={handleNavigate} />
          </div>
        );
      case 'hanuman-bahuk':
        return (
          <div className="page-container">
            <HanumanBahukBook onNavigate={handleNavigate} />
          </div>
        );
      case 'vanyagraamya-pashu':
        return (
          <div className="page-container">
            <VanyagraamyaPashuBook onNavigate={handleNavigate} />
          </div>
        );
      case 'nishedha-madira':
        return (
          <div className="page-container">
            <NishedhaMadiraBook onNavigate={handleNavigate} />
          </div>
        );
      case 'saagar-manthan':
        return (
          <div className="page-container">
            <SaagarManthanBook onNavigate={handleNavigate} />
          </div>
        );
      case 'geedh':
        return (
          <div className="page-container">
            <GedhBook onNavigate={handleNavigate} />
          </div>
        );
      case 'maataa-pitaa-guru':
        return (
          <div className="page-container">
            <MaataaPitaaGuruBook onNavigate={handleNavigate} />
          </div>
        );
      case 'nrivarg':
        return (
          <div className="page-container">
            <NrivargBook onNavigate={handleNavigate} />
          </div>
        );
      case 'kshatriyavarg':
        return (
          <div className="page-container">
            <KshatriyavargBook onNavigate={handleNavigate} />
          </div>
        );
      case 'vaishyavarg-khand1':
        return (
          <div className="page-container">
            <VaishyavargKhand1Book onNavigate={handleNavigate} />
          </div>
        );
      case 'vaishyavarg-khand2':
        return (
          <div className="page-container">
            <VaishyavargKhand2Book onNavigate={handleNavigate} />
          </div>
        );
      case 'vaishyavarg-khand3':
        return (
          <div className="page-container">
            <VaishyavargKhand3Book onNavigate={handleNavigate} />
          </div>
        );
      case 'parashar-geeta':
        return (
          <div className="page-container">
            <ParasharGeetaBook onNavigate={handleNavigate} />
          </div>
        );
      case 'panchakosha-workbook':
        return (
          <div className="page-container">
            <PanchakoshaWorkbookBook onNavigate={handleNavigate} />
          </div>
        );
      case 'laukika-nyaya':
        return (
          <div className="page-container">
            <LaukikaNyayaBook onNavigate={handleNavigate} />
          </div>
        );
      case 'arya-laghukhaga':
        return (
          <div className="page-container">
            <AryaLaghukhagaBook onNavigate={handleNavigate} />
          </div>
        );
      case 'arya-svarnamanjusha':
        return (
          <div className="page-container">
            <AryaSvarnamanjushaBook onNavigate={handleNavigate} />
          </div>
        );
      case 'arya-rahasyamayi-guha':
        return (
          <div className="page-container">
            <AryaRahasyamayiGuhaBook onNavigate={handleNavigate} />
          </div>
        );
      case 'educational-trips':
        return (
          <div className="page-container">
            <EducationalTrips onNavigate={handleNavigate} />
          </div>
        );
      case 'sacred-sites':
        return (
          <div className="page-container">
            <SacredSites onNavigate={handleNavigate} />
          </div>
        );
      case 'impact':
        return (
          <div className="page-container">
            <ImpactDashboard onNavigate={handleNavigate} />
          </div>
        );
      case 'booking':
        return (
          <div className="page-container">
            <BookingFlow onNavigate={handleNavigate} />
          </div>
        );
      case 'admin':
        return (
          <div className="page-container">
            <AdminDashboard onNavigate={handleNavigate} />
          </div>
        );
      case 'sanskrit-translator':
        return (
          <div className="page-container">
            <SanskritTranslator onNavigate={handleNavigate} />
          </div>
        );
      case 'public-profile':
        return (
          <div className="page-container">
            <PublicProfileView alias={viewAlias || undefined} />
          </div>
        );
      default:
        return <div>{t('app.error.notFound')}</div>;
    }
  };

  const localeLabels = useMemo<Record<string, string>>(
    () => ({
      hi: 'हिन्दी',
      en: 'English',
      sa: 'संस्कृत',
      as: 'অসমীয়া',
      bn: 'বাংলা',
      brx: 'बड़ो',
      doi: 'डोगरी',
      gu: 'ગુજરાતી',
      kn: 'ಕನ್ನಡ',
      ks: 'کٲشُر',
      kok: 'कोंकणी',
      mai: 'मैथिली',
      ml: 'മലയാളം',
      mni: 'মৈতৈলোন্',
      mr: 'मराठी',
      ne: 'नेपाली',
      or: 'ଓଡ଼ିଆ',
      pa: 'ਪੰਜਾਬੀ',
      sat: 'ᱥᱟᱱᱛᱟᱲᱤ',
      sd: 'سنڌي',
      ta: 'தமிழ்',
      te: 'తెలుగు',
      ur: 'اردو',
    }),
    [],
  );

  function safeText(key: string, fallback: string) {
    const value = t(key);
    return value === key ? fallback : value;
  }

  const navControls = (
    <div className="nav-controls" role="group" aria-label={safeText('nav.toolbar.label', 'Site controls')}>
      <select
        className="nav-control nav-control--select"
        value={locale}
        onChange={(e) => setLocale(e.target.value)}
        aria-label={safeText('nav.locale.label', 'Website language')}
        title={safeText('nav.locale.label', 'Website language')}
      >
        {locales.map((item) => (
          <option key={item} value={item}>{localeLabels[item] ?? item}</option>
        ))}
      </select>

      <button
        className="nav-auth-btn nav-theme-btn nav-control"
        onClick={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      >
        {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
      </button>
    </div>
  );

  // ── Auth slot for the nav bar ─────────────────────────────────────
  const authGate = user && profile ? calculateGatewayProgress(profile, policy) : null;
  const pendingSteps = authGate && !authGate.ready ? authGate.total - authGate.completed : 0;
  const sevaProviderStatus = profile ? computeSevaProviderStatus(profile) : null;

  const authSlot = authLoading ? null : user && profile ? (
    <div className="nav-auth-wrapper" ref={userMenuRef}>
      <button
        className="nav-auth-btn"
        onClick={() => setShowUserMenu(v => !v)}
        aria-label={t('auth.menu')}
        title={pendingSteps > 0 ? safeText('nav.pending.tooltip', `${pendingSteps} profile steps pending`) : undefined}
      >
        <span className="nav-auth-btn-inner">
          <span
            className="nav-auth-initials"
            style={{ background: `hsl(${(profile.uid.charCodeAt(0) * 37 + profile.uid.charCodeAt(1) * 17) % 360},55%,55%)` }}
          >
            {profile.alias ? profile.alias.slice(0, 1).toUpperCase() : '👤'}
          </span>
          {pendingSteps > 0 && (
            <span className="nav-pending-badge" aria-label={`${pendingSteps} pending`}>{pendingSteps}</span>
          )}
          {sevaProviderStatus?.cachedReviewStatus === 'pending' && (
            <span className="nav-seva-badge nav-seva-badge--pending" title={safeText('seva.provider.badge.pending.tooltip', 'Seva provider review pending')}>🤝</span>
          )}
          {sevaProviderStatus?.cachedReviewStatus === 'approved' && (
            <span className="nav-seva-badge nav-seva-badge--approved" title={safeText('seva.provider.badge.approved.tooltip', 'Seva provider approved')}>✅</span>
          )}
        </span>
        <span>{profile.alias || 'मैं'}</span>
      </button>
      {showUserMenu && (
        <div className="nav-user-dropdown">
          {pendingSteps > 0 && (
            <button className="nav-dropdown-pending" onClick={() => { setShowUserMenu(false); handleNavigate('/profile'); }}>
              ⚠️ {safeText('nav.pending.action', `${pendingSteps} steps to complete`)}
            </button>
          )}
          {sevaProviderStatus?.cachedReviewStatus === 'pending' && (
            <button className="nav-dropdown-seva-pending" onClick={() => { setShowUserMenu(false); handleNavigate('/profile'); }}>
              🤝 {safeText('seva.provider.badge.pending', 'Seva activation under review')}
            </button>
          )}
          {sevaProviderStatus?.cachedReviewStatus === 'approved' && (
            <button className="nav-dropdown-seva-approved" onClick={() => { setShowUserMenu(false); handleNavigate('/seva-gigs'); }}>
              ✅ {safeText('seva.provider.badge.approved', 'Seva provider — active')}
            </button>
          )}
          {profile.roleGateway?.selectedRole === 'raja' && (
            <button className="nav-dropdown-raja" onClick={() => { setShowUserMenu(false); handleNavigate('/raja-dashboard'); }}>
              🏛 {safeText('raja.dashboard.nav.label', 'Raja Governance')}
            </button>
          )}
          {isAdmin && !adminLoading && (
            <button onClick={() => { setShowUserMenu(false); handleNavigate('/admin'); }}>
              ⚙ Admin
            </button>
          )}
          <button onClick={() => { setShowUserMenu(false); handleNavigate('/profile'); }}>
            👤 {t('auth.profile')}
          </button>
          <button onClick={() => { setShowUserMenu(false); handleNavigate('/discovery'); }}>
            🤝 {t('auth.discovery')}
          </button>
          <button onClick={() => { setShowUserMenu(false); handleNavigate('/wall-feed'); }}>
            📰 Wall Feed
          </button>
        </div>
      )}
    </div>
  ) : (
    /* ── X.com + original login button side-by-side ── */
    <div className="nav-auth-group flex items-center gap-3">
      <XSignInButton />

      <button
        className="nav-auth-btn"
        onClick={() => {
          if (getConsentRecord()) {
            setShowLoginModal(true);
          } else {
            setShowConsentNotice(true);
          }
        }}
        aria-label={t('auth.login')}
      >
        🔐 {t('auth.login')}
      </button>
    </div>
  );

  const localizedConfig = useMemo(
    () => (state.config ? localizePortalConfig(state.config, t, locales) : null),
    [state.config, t, locales],
  );

  const navText = useCallback((key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  }, [t]);

  const portalNav: NavItem[] = useMemo(() => [
    { label: navText('nav.home', 'Home'), path: '/', icon: '🏠' },
    { label: navText('nav.products', 'उत्पाद'), path: '/products', icon: '🛍️' },
    {
      label: navText('nav.gurukul', 'गुरुकुल'),
      path: '/products',
      icon: '🏛️',
      description: navText('nav.gurukul.desc', 'Learning, statecraft, Sanskrit, and self-realisation'),
      children: [
        { label: navText('nav.gurukul.dashboard', '12 Dimensions Dashboard'), path: '/products', icon: '◉', description: navText('nav.gurukul.dashboard.desc', 'A shared map of the 12-life-dimension model') },
        { label: navText('nav.gurukul.statecraft', 'Statecraft'), path: '/journey', icon: '⚖️', description: navText('nav.gurukul.statecraft.desc', 'Role growth, governance, and duty') },
      ],
    },
    {
      label: navText('nav.sthana', 'स्थान'),
      path: '/contact',
      icon: '📍',
      description: navText('nav.sthana.desc', 'Burnaby, Jaynagar, Ranchi, Delhi, and Dharamshala'),
      children: [
        { label: navText('nav.sthana.burnaby', 'Burnaby'), path: '/contact?place=burnaby', icon: '◉', description: navText('nav.sthana.live', 'Live telemetry placeholder') },
        { label: navText('nav.sthana.jaynagar', 'Jaynagar'), path: '/contact?place=jaynagar', icon: '◉', description: navText('nav.sthana.live', 'Live telemetry placeholder') },
        { label: navText('nav.sthana.ranchi', 'Ranchi'), path: '/contact?place=ranchi', icon: '◉', description: navText('nav.sthana.live', 'Live telemetry placeholder') },
        { label: navText('nav.sthana.delhi', 'Delhi'), path: '/contact?place=delhi', icon: '◉', description: navText('nav.sthana.live', 'Live telemetry placeholder') },
        { label: navText('nav.sthana.dharamshala', 'Dharamshala'), path: '/contact?place=dharamshala', icon: '◉', description: navText('nav.sthana.live', 'Live telemetry placeholder') },
      ],
    },
    {
      label: navText('nav.resources', 'संसाधन'),
      path: '/curriculum',
      icon: '📚',
      description: navText('nav.resources.desc', 'Vedas, Puranas, community, and all learning resources'),
      children: [
        { label: navText('nav.resources.youtube', 'YouTube'), path: 'https://youtube.com/@raam_sevak', icon: '▶', description: navText('nav.resources.youtube.desc', 'Raam Sevak channel — discourses, sessions, and uploads') },
        { label: navText('nav.resources.x', 'X Community'), path: 'https://x.com/i/communities/1981771124343283876', icon: '𝕏', description: navText('nav.resources.x.desc', 'Community discussions and announcements on X') },
        { label: navText('nav.sangati.community', 'Community'), path: '/sangati', icon: '•', description: navText('nav.sangati.community.desc', 'Shared practice and mutual support') },
        { label: navText('nav.sangati.sessions', 'Live Sessions'), path: '/tutor', icon: '⏺', description: navText('nav.sangati.sessions.desc', 'Guided live learning sessions') },
        { label: navText('nav.vritti.opportunities', 'Opportunities'), path: '/careers', icon: '✦', description: navText('nav.vritti.opportunities.desc', 'Open roles and project openings') },
        { label: navText('nav.vritti.seva_gigs', 'Seva Gigs'), path: '/seva-gigs', icon: '🤝', description: navText('nav.vritti.seva_gigs.desc', 'Service provider marketplace — Electrician, Plumber, Carpenter & more') },
        { label: navText('nav.vritti.talent', 'Talent Pool'), path: '/discovery', icon: '◌', description: navText('nav.vritti.talent.desc', 'People, skills, and collaboration paths') },
      ],
    },
  ], [navText]);

  const searchIndex = useMemo(() => {
    const items: Array<{ label: string; path: string; icon?: string; description?: string; keywords?: string[] }> = [
      ...portalNav,
      ...(portalNav.flatMap((item) => item.children ?? [])),
      { label: navText('footer.feedback', 'स्वयं निरीक्षण'), path: '/contact', keywords: ['feedback', 'insight', 'observe'] },
      { label: navText('cta.donate', 'Donate'), path: '/contact', keywords: ['support', 'giving'] },
      { label: navText('cta.joinOpenSangati', 'Join Open Sangati'), path: '/sangati', keywords: ['community', 'join'] },
    ];
    return items.map((item) => ({
      label: item.label,
      path: item.path,
      icon: item.icon,
      keywords: [item.label, ...(item.description ? [item.description] : []), ...(item.keywords ?? [])],
    }));
  }, [portalNav, navText]);

  // dimensionStrip removed per UX decision — strip row hidden from navigation

  const footerColumns = useMemo(() => ([
    {
      title: navText('nav.home', 'Home'),
      links: [
        { label: navText('nav.home', 'Home'), url: '/' },
        { label: navText('nav.grantha', 'Rajdharma Grantha'), url: '/curriculum' },
      ],
    },
    {
      title: navText('nav.gurukul', 'गुरुकुल'),
      links: [
        { label: navText('nav.gurukul.dashboard', '12 Dimensions Dashboard'), url: '/products' },
        { label: navText('nav.gurukul.statecraft', 'Statecraft'), url: '/journey' },
      ],
    },
    {
      title: navText('nav.sthana', 'स्थान'),
      links: [
        { label: navText('nav.sthana.burnaby', 'Burnaby'), url: '/contact?place=burnaby' },
        { label: navText('nav.sthana.jaynagar', 'Jaynagar'), url: '/contact?place=jaynagar' },
        { label: navText('nav.sthana.ranchi', 'Ranchi'), url: '/contact?place=ranchi' },
        { label: navText('nav.sthana.delhi', 'Delhi'), url: '/contact?place=delhi' },
        { label: navText('nav.sthana.dharamshala', 'Dharamshala'), url: '/contact?place=dharamshala' },
      ],
    },
    {
      title: navText('nav.resources', 'संसाधन'),
      links: [
        { label: navText('nav.sangati.community', 'Community'), url: '/sangati' },
        { label: navText('nav.vritti.opportunities', 'Opportunities'), url: '/careers' },
        { label: navText('nav.vritti.seva_gigs', 'Seva Gigs'), url: '/seva-gigs' },
        { label: navText('nav.vritti.talent', 'Talent Pool'), url: '/discovery' },
      ],
    },
  ]), [navText]);

  return (
    <div className="app">
      {!state.error && (
        <DynamicNav
          items={portalNav}
          currentPath={window.location.pathname}
          onNavigate={handleNavigate}
          authSlot={authSlot}
          controlsSlot={navControls}
          searchIndex={searchIndex}
          ctas={[
            { label: navText('cta.donate', 'Donate'), path: '/contact' },
            ...(!user ? [{ label: navText('cta.joinOpenSangati', 'Join Open Sangati'), path: '/sangati' }] : []),
          ]}
          dimensionStrip={[]}
          brand={navText('brand.name', 'Raajadharma Gurukul')}
          searchPlaceholder={navText('search.placeholder', 'Search Sanskrit, Hindi, or English')}
          onSearchOpen={() => setSearchOpen(true)}
        />
      )}

      {renderPage()}

      <SearchModal
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={(path) => { setSearchOpen(false); handleNavigate(path); }}
        token={searchToken}
      />

      {showConsentNotice && (
        <ConsentNotice
          onAccept={() => { setShowConsentNotice(false); setShowLoginModal(true); }}
          onDecline={() => setShowConsentNotice(false)}
        />
      )}

      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}

      {!state.error && (
        <footer className="app-footer">
          <p className="app-footer-trust">
            🛡️ {navText('footer.trust', 'Our Programs (धनुर्वेद & संस्कृत संवाद) embody Rajadharma — Secure, Reliable, Timeless')}
          </p>
          <p>{localizedConfig?.footer.copyright ?? '© Raajadharma Gurukul'}</p>
          <div className="footer-sitemap">
            {footerColumns.map((column) => (
              <div key={column.title} className="footer-column">
                <h3>{column.title}</h3>
                <div className="footer-links">
                  {column.links.map((link) => (
                    <a key={link.url} href={link.url} onClick={(e) => { e.preventDefault(); handleNavigate(link.url); }}>
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="footer-links footer-links--meta">
            <a href="https://youtube.com/@raam_sevak" target="_blank" rel="noreferrer">YouTube</a>
            <a href="https://x.com/i/communities/1981771124343283876" target="_blank" rel="noreferrer">X</a>
            <a href="/contact?feedback=1" onClick={(e) => { e.preventDefault(); handleNavigate('/contact?feedback=1'); }}>
              {navText('footer.feedback', 'स्वयं निरीक्षण')}
            </a>
            <a href="/privacy" onClick={(e) => { e.preventDefault(); handleNavigate('/privacy'); }}>
              {navText('footer.privacy', 'Privacy Policy')}
            </a>
            <a href="/terms" onClick={(e) => { e.preventDefault(); handleNavigate('/terms'); }}>
              {navText('footer.terms', 'Terms of Service')}
            </a>
            <a href="/data-deletion" onClick={(e) => { e.preventDefault(); handleNavigate('/data-deletion'); }}>
              {navText('footer.data_deletion', 'Request Data Deletion')}
            </a>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
